import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "../lib/api";
import { catLabel, formatPrice } from "../lib/site";
import ProductDialog from "./ProductDialog";

export default function ProductsTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/products").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errorText(e)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/products/${p.id}`);
    toast.success("Product deleted");
    load();
    onChange?.();
  };

  return (
    <div className="rounded-2xl border border-navy/10 bg-white">
      <div className="flex items-center justify-between border-b border-navy/10 p-4">
        <p className="text-sm text-slate-600">{rows.length} products · changes appear on the website instantly</p>
        <button data-testid="admin-product-add-button" onClick={() => setEditing({})} className="inline-flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark"><Plus className="h-4 w-4" /> Add product</button>
      </div>
      <div className="overflow-x-auto">
        <table data-testid="admin-products-table" className="w-full min-w-[720px] text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="p-3">Product</th><th className="p-3">Category</th><th className="p-3">Pack</th><th className="p-3">Price</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-t border-navy/5">
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <img src={p.image || "/brand/mhm-logo.jpg"} alt="" className="h-12 w-12 rounded-lg object-contain bg-slate-100" />
                    <span className="font-semibold text-navy">{p.name}{p.featured && <span className="ml-2 rounded bg-brand-soft px-1.5 py-0.5 text-[10px] font-bold uppercase text-brand">Featured</span>}</span>
                  </div>
                </td>
                <td className="p-3">{catLabel(p.category)}</td>
                <td className="p-3">{p.pack_size}</td>
                <td className="p-3 font-semibold">{formatPrice(p.price) ?? <span className="text-slate-400">On request</span>}</td>
                <td className="p-3">{p.in_stock ? <span className="text-emerald-600">In stock</span> : <span className="text-red-600">Out of stock</span>}</td>
                <td className="p-3">
                  <div className="flex justify-end gap-1">
                    <button data-testid={`admin-product-edit-${p.id}`} onClick={() => setEditing(p)} className="rounded-lg p-2 hover:bg-slate-100" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`admin-product-delete-${p.id}`} onClick={() => remove(p)} className="rounded-lg p-2 text-red-600 hover:bg-red-50" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ProductDialog product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); onChange?.(); }} />
    </div>
  );
}
